import { useCallback, useEffect, useRef, useState } from "react"
import { publicConsumerApi } from "@/lib/api/public-consumer"
import type { ApiEstimate } from "@/lib/api/types"

// The desktop watching the phone: reads the lead every couple of seconds
// while the tab is visible. Stops once the phone has sent its photos, and
// after 15 minutes without any change ("Still there?" resumes it) so a
// forgotten tab doesn't poll forever.

const EVERY_MS = 2000
const IDLE_AFTER_MS = 15 * 60 * 1000

export function useEstimateWatch(token: string | null, { stopWhen }: { stopWhen: (e: ApiEstimate) => boolean }) {
  const [estimate, setEstimate] = useState<ApiEstimate | null>(null)
  const [idle, setIdle] = useState(false)
  const [stopped, setStopped] = useState(false)
  const lastChange = useRef(Date.now())
  const lastSnapshot = useRef("")
  const stopRef = useRef(stopWhen)
  stopRef.current = stopWhen

  useEffect(() => {
    if (!token || idle || stopped) return
    let alive = true
    let timer: ReturnType<typeof setTimeout> | null = null

    const tick = async () => {
      if (!alive) return
      if (document.visibilityState === "visible") {
        try {
          const next = await publicConsumerApi.getEstimate(token)
          if (!alive) return
          const snapshot = JSON.stringify([next.images, next.metadata, next.status])
          if (snapshot !== lastSnapshot.current) {
            lastSnapshot.current = snapshot
            lastChange.current = Date.now()
            setEstimate(next)
          }
          if (stopRef.current(next)) {
            setStopped(true)
            return
          }
        } catch {
          // A missed read is fine; the next one catches up.
        }
        if (Date.now() - lastChange.current > IDLE_AFTER_MS) {
          setIdle(true)
          return
        }
      }
      timer = setTimeout(() => void tick(), EVERY_MS)
    }
    void tick()
    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [token, idle, stopped])

  const resume = useCallback(() => {
    lastChange.current = Date.now()
    setIdle(false)
  }, [])

  return { estimate, idle, resume }
}
